// Error recovery and retry utilities

export interface RetryOptions {
  maxRetries?: number;
  delayMs?: number;
  exponentialBackoff?: boolean;
  onRetry?: (attempt: number, error: any) => void;
}

// Detect if error is network-related
export function isNetworkError(error: any): boolean {
  if (!error) return false;

  const networkErrors = [
    'network error',
    'failed to fetch',
    'networkerror',
    'network request failed',
    'timeout',
    'econnrefused',
    'enotfound',
    'econnreset'
  ];

  const errorMessage = (error.message || error.toString()).toLowerCase();
  return networkErrors.some(msg => errorMessage.includes(msg));
}

// Check if user is online
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine;
}

// Retry operation with exponential backoff
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    delayMs = 1000,
    exponentialBackoff = true,
    onRetry
  } = options;

  let lastError: any;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Check if online before attempting
      if (!isOnline()) {
        throw new Error('אין חיבור לאינטרנט');
      }

      return await operation();
    } catch (error: any) {
      lastError = error;

      // Don't retry on validation errors or 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        throw error;
      }

      // Only retry on network errors
      if (!isNetworkError(error)) {
        throw error;
      }

      // If last attempt, throw error
      if (attempt === maxRetries - 1) {
        throw error;
      }

      // Calculate delay
      const delay = exponentialBackoff
        ? delayMs * Math.pow(2, attempt)
        : delayMs;

      // Notify about retry
      if (onRetry) {
        onRetry(attempt + 1, error);
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// Save data to localStorage as backup
export function saveToLocalStorage(key: string, data: any): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {

  }
}

// Load data from localStorage backup
export function loadFromLocalStorage<T>(key: string): T | null {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {

    return null;
  }
}

// Clear localStorage backup
export function clearLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {

  }
}

// Offline mode detection hook data
export interface OfflineStatus {
  isOffline: boolean;
  wasOffline: boolean;
}

// Check and track offline status
export function getOfflineStatus(): OfflineStatus {
  const isOffline = !isOnline();
  const wasOffline = localStorage.getItem('was_offline') === 'true';

  if (isOffline) {
    localStorage.setItem('was_offline', 'true');
  } else if (wasOffline) {
    localStorage.removeItem('was_offline');
  }

  return { isOffline, wasOffline };
}

// Create retry-enabled operation
export function createRetryableOperation<T>(
  operation: () => Promise<T>,
  backupKey?: string
): () => Promise<T> {
  return async () => {
    try {
      const result = await retryWithBackoff(operation, {
        maxRetries: 3,
        delayMs: 1000,
        exponentialBackoff: true,
        onRetry: (attempt, error) => {

        }
      });

      // Clear backup on success
      if (backupKey) {
        clearLocalStorage(backupKey);
      }

      return result;
    } catch (error) {
      // Save to localStorage as backup on failure
      if (backupKey && isNetworkError(error)) {

        throw new Error('הפעולה נכשלה. הנתונים נשמרו מקומית וינסו להישלח שוב מאוחר יותר');
      }

      throw error;
    }
  };
}

// Timeout wrapper for promises
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number = 30000,
  timeoutError: string = 'הפעולה לקחה יותר מדי זמן'
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(timeoutError));
    }, timeoutMs);

    promise
      .then(value => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(error => {
        clearTimeout(timer);
        reject(error);
      });
  });
}
